import React, { useEffect, useState } from "react";

import {
  AcceptanceButton,
  ViewButton,
} from "layouts/AcceptancePage/components";
import { AcceptedCheckMarkIcon } from "@hubcapp/views/AcceptancePageComponent/components/DocumentList/AcceptedCheckMarkIcon";
import {
  Box,
  Card,
  CardContent,
  SvgIcon,
  Typography,
  makeStyles,
} from "@material-ui/core";
import { DocumentForSigning } from "../types";
import { SignedUrl } from "@hubcapp/graphql/types/gateway";
import CheckMark from "../../UI/CheckMark";
import clsx from "clsx";

interface Props {
  brand: string;
  documents: SignedUrl[] | undefined;
  onAllDocumentsRead: () => void;
}

const useStyles = makeStyles(theme => ({
  buttonsWrapper: {
    display: "flex",
    gap: "16px",
    width: "234px",
  },
  confirmedBtn: {
    display: "flex",
    alignItems: "center",
    padding: "10px 12px",
    fontSize: "18px",
    fontWeight: 700,
    "&.aa": {
      gap: 6,
    },
    "&.bsm": {
      fontFamily: "Poppins !important",
      fontWeight: 400,
      gap: 12,
    },

    [theme.breakpoints.down("sm")]: {
      fontSize: "18px",
    },
    "& .MuiSvgIcon-root": {
      width: "24px",
      height: "auto",
      [theme.breakpoints.down("sm")]: {
        width: "24px",
      },
    },
  },
  item: {
    marginTop: "32px",
    width: "569px",
    [theme.breakpoints.down("sm")]: {
      width: "100%",
      marginTop: "63px",
      boxShadow: "none",
    },
  },
  itemContent: {
    padding: "16px !important",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "16px",
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      alignItems: "start",
      gap: "0",
    },
  },
  itemTitle: {
    fontWeight: 400,
    fontSize: "18px",
    lineHeight: "24px",
    "&.bsm": {
      fontFamily: "Poppins !important",
    },
    [theme.breakpoints.down("sm")]: {
      fontSize: "16px",
      width: "100%",
      marginBottom: "17px",
    },
  },
}));

const documentsForSigning: DocumentForSigning[] = [
  {
    label: "Pre-contract Information (PCI)",
    fileNameQuery: ["PCI"],
  },
  {
    label: "Driver Handbook",
    fileNameQuery: ["Driver", "Handbook"],
  },
  {
    label: "Service Intervals",
    fileNameQuery: ["Service", "Intervals"],
  },
  {
    label: "Franchise Agreement",
    fileNameQuery: ["Franchise", "Agreement"],
  },
];

const DocumentList: React.FC<Props> = ({
  brand,
  documents,
  onAllDocumentsRead: onAllDocumentsRead,
}) => {
  const classes = useStyles();

  const [documentsList, setDocuments] = useState<DocumentForSigning[]>(
    documentsForSigning,
  );

  useEffect(() => {
    if (!documents) {
      return;
    }
    setDocuments(state =>
      state.map(doc => ({
        ...doc,
        url: documents?.find(d =>
          doc.fileNameQuery.every(key => d.documentName.includes(key)),
        )?.url,
      })),
    );
  }, [documents]);

  const setDocumentAllowedToRead = (documentIndex: number) =>
    setDocuments(state => {
      const newState = [...state];
      newState[documentIndex].allowRead = true;

      return newState;
    });

  const setDocumentRead = (documentIndex: number) =>
    setDocuments(state => {
      const newState = [...state];
      newState[documentIndex].read = true;

      if (newState.every(doc => doc.read)) {
        onAllDocumentsRead();
      }

      return newState;
    });

  if (!documents) {
    return <Typography>Loading documents...</Typography>;
  }

  return (
    <div>
      {documentsList.map((document, documentIndex) => (
        <Card key={documentIndex} elevation={3} className={classes.item}>
          <CardContent className={classes.itemContent}>
            <Box>
              <Typography
                variant="h1"
                className={clsx(classes.itemTitle, {
                  bsm: brand === "BSM",
                  aa: brand === "AADS",
                })}
              >
                {documentIndex + 1}. {document.label}
              </Typography>
            </Box>
            <Box className={classes.buttonsWrapper}>
              <ViewButton
                brand={brand}
                disabled={
                  documentIndex > 0 && !documentsList[documentIndex - 1]?.read
                }
                onClick={() => {
                  setDocumentAllowedToRead(documentIndex);
                  window.open(document.url, "_blank");
                }}
              >
                View
              </ViewButton>
              {document.read ? (
                <div
                  data-brand={brand}
                  className={clsx(classes.confirmedBtn, {
                    bsm: brand === "BSM",
                    aa: brand === "AADS",
                  })}
                >
                  Accepted
                  {brand === "BSM" ? (
                    <AcceptedCheckMarkIcon />
                  ) : (
                    <SvgIcon htmlColor="black" viewBox="0 0 49 49">
                      <CheckMark />
                    </SvgIcon>
                  )}
                </div>
              ) : (
                <AcceptanceButton
                  brand={brand}
                  onClick={() => {
                    setDocumentRead(documentIndex);
                  }}
                  disabled={!documentsList[documentIndex]?.allowRead}
                >
                  Accept
                </AcceptanceButton>
              )}
            </Box>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

export default DocumentList;
